import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import PropTypes from 'prop-types';
import { Alert, Loader, Tabs } from '@itds/elements/index.js';

import Config from '@/config';

import CustomActions from './CustomActions';
import General from './General';
import LimitCarriers from './LimitCarriers';
import LimitProductTypes from './LimitProductTypes';
import EmbedCode from './EmbedCode';

import LimitStates from '@brokerage/components/LimitStates';

import { Api } from '@brokerage/utils/Api';

export const Show = ({
  detail,
  token: propToken,
  controlledTabs,
  controlledTabKey,
  onTabChange,
  onParentSave // <-- add this prop
}) => {
  const params = useParams();
  const [bga_quoter, setBgaQuoter] = useState(detail || {});
  const [error_message, setErrorMessage] = useState();
  const [success_message, setSuccessMessage] = useState();
  const [loading, setLoading] = useState(!detail);
  // Set default tab: 'general' for standalone, 'product_defaults' for controlled
  const [activeTab, setActiveTab] = useState(
    controlledTabKey || (controlledTabs ? 'product_defaults' : 'general')
  );

  useEffect(() => {
    if (!detail) {
      loadBgaQuoter();
    }
  }, [params.id, detail]);

  useEffect(() => {
    if (controlledTabKey) setActiveTab(controlledTabKey);
  }, [controlledTabKey]);

  useEffect(() => {
    return () => {
      setBgaQuoter({});
    };
  }, []);

  const loadBgaQuoter = () => {
    Api.loadBgaQuoter(params.id).then(({ data }) => {
      setBgaQuoter(data);
      setLoading(false);
    });
  };

  const handleStatesChange = ({ default_state, states }) => {
    handleSave({ default_state, states });
  };

  const handleSave = input_fields => {
    window.scrollTo(0, 0);
    // If in controlled mode, call parent handleSave
    if (controlledTabs && typeof onParentSave === 'function') {
      onParentSave(
        input_fields,
        // Provide callbacks for error/success so parent can set messages
        {
          setErrorMessage,
          setSuccessMessage
        }
      );
      return;
    }
    if (detail) {
      return;
    }
    Api.updateBgaQuoter(params.id, input_fields)
      .then(() => {
        loadBgaQuoter();
        setErrorMessage(null);
        setSuccessMessage('ALQ updated successfully');
        setTimeout(() => {
          setSuccessMessage(null);
        }, 2000);
      })
      .catch(() => {
        setSuccessMessage(null);
        setErrorMessage('Unable to update ALQ');
        setTimeout(() => {
          setErrorMessage(null);
        }, 2000);
      });
  };

  // Tabs for controlled (Universal Quoter) mode
  const uqTabs = [
    { label: 'Product Defaults', key: 'product_defaults' },
    { label: 'Limit Carriers', key: 'limit_carriers' },
    { label: 'Limit Product Types', key: 'limit_product_types' },
    { label: 'Limit States', key: 'limit_states' },
    { label: 'Custom Actions', key: 'custom_actions' }
  ];
  // Tabs for standalone mode
  const standaloneTabs = [
    { label: 'General', key: 'general' },
    { label: 'Limit Carriers', key: 'limit_carriers' },
    { label: 'Limit Product Types', key: 'limit_product_types' },
    { label: 'Limit States', key: 'limit_states' },
    { label: 'Custom Actions', key: 'custom_actions' },
    { label: 'Preview', key: 'preview' },
    { label: 'Embed Code', key: 'embed_code' }
  ];
  const tabs = controlledTabs ? uqTabs : standaloneTabs;

  // Map tab key to component
  const isControlled = !!controlledTabs;
  const quoterData = isControlled ? detail : bga_quoter;
  const child_props = {
    bga_quoter: quoterData,
    handleSave,
    loadBgaQuoter,
    setBgaQuoter,
    setErrorMessage,
    setSuccessMessage
  };
  const tabContent = {
    product_defaults: (
      <General {...child_props} controlledMode={isControlled && activeTab === 'product_defaults'} />
    ), // Universal Quoter mode
    general: <General {...child_props} controlledMode={false} />, // Standalone mode
    limit_carriers: (
      <LimitCarriers {...child_props} bga_quoter={quoterData} handleSave={handleSave} />
    ), // use latest data
    limit_product_types: (
      <LimitProductTypes {...child_props} handleSave={handleSave} quoter={quoterData} />
    ),
    limit_products: (
      <LimitProductTypes {...child_props} handleSave={handleSave} quoter={quoterData} />
    ),
    limit_states: (
      <LimitStates
        defaultState={quoterData.default_state}
        handleSave={handleSave}
        selectedStates={quoterData.states}
      />
    ),
    custom_actions: <CustomActions {...child_props} />,
    preview: null, // No-op in render
    embed_code: !controlledTabs ? <EmbedCode {...child_props} /> : null
  };

  // Handle tab click
  const handleTabClick = tabKey => {
    if (!controlledTabs && tabKey === 'preview') {
      window.open(`${Config.ALQ_URL}?token=${propToken || bga_quoter.token}`);
      setActiveTab('general');
      if (onTabChange) onTabChange('general');
      return;
    }
    setActiveTab(tabKey);
    if (onTabChange) onTabChange(tabKey);
  };

  return loading ? (
    <Loader />
  ) : (
    <>
      {error_message ? <Alert type='error'>{error_message}</Alert> : null}
      {success_message ? <Alert type='success'>{success_message}</Alert> : null}
      {!controlledTabs ? (
        <div style={{ display: 'flex', justifyContent: 'space-between' }}>
          <Tabs
            tabs={tabs.map(tab => ({
              label: tab.label,
              to: tab.key,
              isActive: activeTab === tab.key,
              onClick: () => handleTabClick(tab.key)
            }))}
            vertical={true}
          />
          <div style={{ flex: 1, marginLeft: 40 }}>{tabContent[activeTab]}</div>
        </div>
      ) : (
        <>{tabContent[activeTab]}</>
      )}
    </>
  );
};

Show.propTypes = {
  controlledTabKey: PropTypes.string,
  controlledTabs: PropTypes.array,
  detail: PropTypes.object,
  name: PropTypes.string,
  onParentSave: PropTypes.func,
  onTabChange: PropTypes.func,
  token: PropTypes.string
};

Show.General = General;
export default Show;
